import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, FormikProps } from 'formik';
import { Button, FormControl, Grid, InputLabel } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import moment from 'moment';

import { RunReportInput } from 'api';
import { DatePickers } from 'components';

interface IPropsEnhanced extends WithStyles<typeof styles> {
  formik: FormikProps<RunReportInput>;
}

const MAX_DATE = moment()
  .endOf('day')
  .subtract(1, 'day')
  .format();

const styles = (theme: Theme) =>
  createStyles({
    button: {
      marginTop: theme.spacing(2),
      borderRadius: 4,
    },
    selectMenu: {
      padding: '10px 16px 9px',
      fontSize: theme.typography.pxToRem(14),
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(connect, withStyles(styles));

const ReportDates: FC<IPropsEnhanced> = ({ classes, formik }) => {
  const [startAnchorEl, setStartAnchorEl] = useState(null);
  const [endAnchorEl, setEndAnchorEl] = useState(null);
  const { t } = useTranslation();

  const { values, setFieldValue } = formik;
  const { start_at, end_at } = values;

  const startDateFormatted = moment.unix(start_at).format('MM/DD/YY');
  const endDateFormatted = moment.unix(end_at).format('MM/DD/YY');
  const startDateLabel = t('admin:startDate');
  const endDateLabel = t('admin:endDate');

  function handleClose() {
    setStartAnchorEl(null);
    setEndAnchorEl(null);
  }

  function handleSave(attribute: string, time: number) {
    const momentValue = moment.unix(time);

    if (attribute === 'start_at') {
      momentValue.startOf('d');
    } else if (attribute === 'end_at') {
      momentValue.endOf('d');
    }

    setFieldValue(attribute, momentValue.unix());
    handleClose();
  }

  return (
    <Grid container justify="space-between">
      <Grid item>
        <FormControl fullWidth margin="normal">
          <InputLabel shrink>{startDateLabel}</InputLabel>
          <Button
            className={classes.button}
            variant="contained"
            onClick={event => setStartAnchorEl(event.currentTarget)}
            endIcon={<ArrowDropDown />}>
            {startDateFormatted}
          </Button>
        </FormControl>
        <DatePickers.DateTime
          value={start_at}
          maxDate={MAX_DATE}
          anchorEl={startAnchorEl}
          onClose={handleClose}
          onSave={time => handleSave('start_at', time)}
        />
      </Grid>
      <Grid item>
        <FormControl fullWidth margin="normal">
          <InputLabel shrink>{endDateLabel}</InputLabel>
          <Button
            className={classes.button}
            variant="contained"
            onClick={event => setEndAnchorEl(event.currentTarget)}
            endIcon={<ArrowDropDown />}>
            {endDateFormatted}
          </Button>
        </FormControl>
        <DatePickers.DateTime
          value={end_at}
          maxDate={MAX_DATE}
          anchorEl={endAnchorEl}
          onClose={handleClose}
          onSave={time => handleSave('end_at', time)}
        />
      </Grid>
    </Grid>
  );
};

export default enhancers(ReportDates);
